import { SubController } from '@hit/amorphic-types';
import { supertypeClass, property, remote, amorphicStatic } from '@haventech/amorphic';
import { AuthenticatingController } from '../../AuthenticatingController';
import {
  AMCAuth0ResponseUser,
  Auth0Client,
  AMCuserType,
  AMCAccountServiceResponse,
  AMCHeavyUserData,
} from '../Auth0Client';
import { Identity } from '../../account/identity/Identity';
import { IdentityService } from '../../account/identity/IdentityService';
import { AuthenticatedPrincipal } from '../../AuthenticatedPrincipal';
import { Auth0Controller } from '../Auth0Controller';
import { AMCIdentityCreator } from './AMCIdentityCreator';
import { LoggerSingleton, Category } from '../../logger/LoggerSingleton';
import { UICategory, UILoggerSingleton } from '../../logger/UILoggerSingleton';

@supertypeClass({ toClient: true, toServer: true })
export class AMCController extends SubController {
  @property()
  private controller: AuthenticatingController;

  @property()
  private identityCreator: AMCIdentityCreator;

  @property({ toClient: true, toServer: false })
  AMCuserInfo: AMCAuth0ResponseUser;

  @property({ toClient: true, toServer: false })
  AMCHeavyUserData: AMCHeavyUserData;

  static new(controller: AuthenticatingController): AMCController {
    return new AMCController().init(controller);
  }

  init(controller: AuthenticatingController): AMCController {
    this.controller = controller;
    return this;
  }

  getIdentityCreator(): AMCIdentityCreator {
    if (!this.identityCreator) {
      this.identityCreator = AMCIdentityCreator.new(IdentityService.getService());
    }
    return this.identityCreator;
  }

  get isAMCheavyUser(): boolean {
    return !!(this.AMCuserInfo && this.AMCuserInfo.user_type && this.AMCuserInfo.user_type === AMCuserType.HEAVY);
  }

  get isAMClightUser(): boolean {
    return !!(this.AMCuserInfo && this.AMCuserInfo.user_type && this.AMCuserInfo.user_type === AMCuserType.LIGHT);
  }

  get hasAMCaccount(): boolean {
    return this.isAMCheavyUser || this.isAMClightUser;
  }

  @remote({ on: 'server' })
  public async getAMCUserInfo(email?: string): Promise<AMCAuth0ResponseUser> {
    const customerEmail: string = email || this.getEmail();
    this.AMCuserInfo = await Auth0Client.getAMCUserInfo(customerEmail);
    return this.AMCuserInfo;
  }

  public async handlePhoneRegistrationForMfa(phone: string): Promise<void> {
    if (this.isAMClightUser && phone) {
      const auth0Id: string = this.AMCuserInfo.user_id;
      const trimPhone = phone.length > 10 ? phone.substring(1) : phone;
      const customerId: string = this.controller.getPrincipal()._id;
      await Auth0Client.registerPhoneForMfa(auth0Id, trimPhone, customerId);
    }
  }

  @remote({ on: 'server' })
  public async getLogoutUrl(): Promise<string> {
    const logoutUrl: string = this.amorphic.config.nconf.get('amcLogoutUrl');
    const logoutReturnToUrl: string = this.amorphic.config.nconf.get('amcLogoutReturnToUrl');
    const auth0ClientId: string = (this.controller as Auth0Controller).auth0clientId;
    return `${logoutUrl}?returnTo=${logoutReturnToUrl}?amc_client=${auth0ClientId}`;
  }

  @remote({ on: 'server' })
  public async updateAMCEmail(): Promise<void> {
    const newEmail: string = this.controller.newEmail;
    const idToken: string = (this.controller as Auth0Controller).auth0IdToken;

    try {
      await Auth0Client.updateAMCEmail(newEmail, idToken);
    } catch (error) {
      const amcError: AMCAccountServiceResponse = error.error as AMCAccountServiceResponse;
      if (amcError && amcError.responseCode === 400) {
        if (amcError.responseDesc.startsWith('This email address is already in use')) {
          throw { code: 'email_registered', text: 'This email is already registered' };
        } else {
          throw { code: 'email_registered', text: amcError.responseDesc };
        }
      }
      throw error;
    }
  }

  @remote({ on: 'server' })
  public async updateAMCPassword(): Promise<void> {
    const password: string = this.controller.password;
    const newPassword: string = this.controller.newPassword;
    const idToken: string = (this.controller as Auth0Controller).auth0IdToken;

    try {
      await Auth0Client.updateAMCPassword(password, newPassword, idToken);
    } catch (error) {
      if (error.statusCode === 400 && error.message && error.message.includes('required field')) {
        throw { code: 'invalid_email_or_password', text: 'Both password fields are required.' };
      }
      const amcError: AMCAccountServiceResponse = error.error as AMCAccountServiceResponse;
      if (amcError) {
        if (amcError.responseCode === 401 && amcError.responseDesc.includes('PasswordHistoryError')) {
          throw { code: 'invalid_email_or_password', text: amcError.responseBody };
        }
        if (amcError.responseCode === 401 || amcError.responseCode === 422) {
          throw { code: 'invalid_email_or_password', text: amcError.responseDesc };
        }
      }
      throw error;
    }
  }

  @remote({ on: 'server' })
  public async resendAMCVerificationEmail(): Promise<void> {
    await Auth0Client.resendAMCVerificationEmail(this.getEmail());
  }

  @remote({ on: 'server' })
  public async whiteListAMCemail(): Promise<void> {
    await Auth0Client.whiteListAMCemail(this.getEmail());
  }

  @remote({ on: 'server' })
  public async setAMCEmailVerified(): Promise<void> {
    await Auth0Client.setAMCEmailVerified(this.getEmail());
  }

  public async getHeavyUserAMCdata(): Promise<void> {
    if (this.AMCuserInfo) {
      this.AMCHeavyUserData = await Auth0Client.getHeavyUserAMCdata(
        this.AMCuserInfo.user_amc_guid,
        this.AMCuserInfo.user_id
      );

      // for non individual users we should not do heavy user flow
      if (this.AMCHeavyUserData.type !== 'I') {
        this.AMCHeavyUserData = null;
        this.AMCuserInfo.user_type = 'light'; // treat as light
        UILoggerSingleton.getInstance().info(
          {
            module: 'AMCController',
            function: 'getHeavyUserAMCdata',
            category: UICategory.request,

            data: {
              auth0_id: this.AMCuserInfo.user_id,
              amc_id: this.AMCuserInfo.user_amc_guid,
            },
          },
          'Snagged AMC heavy user customer identity with PII info, but it is a non-individual type'
        );
      } else {
        UILoggerSingleton.getInstance().info(
          {
            module: 'AMCController',
            function: 'getHeavyUserAMCdata',
            category: UICategory.request,

            data: {
              auth0_id: this.AMCuserInfo.user_id,
              amc_id: this.AMCuserInfo.user_amc_guid,
            },
          },
          'Snagged AMC heavy user customer identity with PII info'
        );
      }
    } else {
      // temp mocking
      this.AMCHeavyUserData = {
        governmentId: '111223321',
        birthDate: '1991-05-19',
        firstName: 'William',
        middleName: 'Sherlock',
        lastName: 'Holmes',
        type: 'I', // do we need to save this on the customer? ¯\_(ツ)_/¯
      };

      UILoggerSingleton.getInstance().info(
        {
          module: 'AMCController',
          function: 'getHeavyUserAMCdata',
          category: UICategory.request,
        },
        'Mocking AMC heavy user data, did not successfully get AMCuserInfo'
      );
    }
  }

  @remote({ on: 'server' })
  public async createIdentityForCustomer(): Promise<void> {
    const principal: AuthenticatedPrincipal = this.controller.getPrincipal() as AuthenticatedPrincipal;

    const auth0Id: string = this.getAuth0Identifier();
    if (auth0Id) {
      const auth0Identity: Identity = await this.getIdentityCreator().createOrGetAuth0Identity(auth0Id, principal);
      const amcId: string = this.getAmcIdentifier();
      if (amcId) {
        await this.getIdentityCreator().createAMCIdentityIfNeeded(amcId, auth0Identity);
      }
    }
  }

  private getAmcIdentifier(): string {
    return this.AMCuserInfo && this.AMCuserInfo.user_amc_guid ? 'amc|' + this.AMCuserInfo.user_amc_guid : null;
  }

  private getAuth0Identifier(): string {
    return this.AMCuserInfo && this.AMCuserInfo.user_id;
  }

  private getEmail(): string {
    return this.controller.getPrincipal().email;
  }

  public async getHeavyUserNameOnly(): Promise<string> {
    return `${this.AMCHeavyUserData.firstName} ${this.AMCHeavyUserData.lastName}`;
  }

  @remote({ on: 'server' })
  public async resetAmcUserInfo(): Promise<void> {
    this.controller.email = undefined;
    this.AMCuserInfo = undefined;
    this.AMCHeavyUserData = undefined;
  }
}
